import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Attachment,
  FinalCost,
  Line,
  MaterialType,
  Province,
  QuotationFireStoreFields,
} from "../types/canvasTypes";

import { getFinalCost, renderChange } from "../util/cost";
import Logo from "../assets/logo.png";
import LogoEnglish from "../assets/logoON.png";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

import HomeLink from "../components/HomeLink";
import i18n from "../i18";
import { t } from "i18next";
import QuotationTable from "../components/QuotationTable";

export type ClientDetails = {
  name: string;
  email: string;
  phone: string;
  address: string;
  note?: string;
  chalkLine: boolean;
  gapSize?: number;
};

const QuotationDetails = () => {
  const { slug } = useParams();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [lines, setLines] = useState<Line[]>([]);
  const [clientDetails, setClientDetails] = useState<ClientDetails>();

  const [date, setDate] = useState<String>("");
  const [finalCost, setFinalCost] = useState<FinalCost[]>([]);
  const [material, setMaterial] = useState<Attachment[]>([]);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [scale, setScale] = useState<number>(1);
  const [selectedLength, setSelectedLength] = useState<string>("42");
  const [province, setProvince] = useState<Province>(Province.Quebec);
  const [materialOption, setMaterialOption] = useState<MaterialType>(
    MaterialType.Silver
  );
  const [includeAttachement, setIncludeAttachement] = useState<boolean>(false);

  const getQuotation = async (quotationId: string) => {
    try {
      const quotationRef = doc(db, "quotations", quotationId);
      // get document
      const quotationDoc = await getDoc(quotationRef);
      // const quotationDoc = await quotationRef.doc(quotationId).get();

      if (quotationDoc.exists()) {
        return quotationDoc.data() as QuotationFireStoreFields;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (slug) {
        const quotation = await getQuotation(slug);
        if (quotation) {
          console.info(quotation);
          setLines(JSON.parse(quotation.lines));
          setClientDetails({
            name: quotation?.name || "",
            email: quotation?.email || "",
            phone: quotation?.phone || "",
            address: quotation?.address || "",
            note: quotation?.note || "",
            chalkLine: quotation?.chalkLine || false,
            gapSize: quotation?.gapSize || 1,
          });
          const milliseconds =
            quotation.date.seconds * 1000 +
            quotation.date.nanoseconds / 1000000;
          const date = new Date(milliseconds);

          setDate(date.toLocaleDateString("fr-FR"));
          if (quotation.material === "Stainless") {
            setMaterialOption(MaterialType.Silver);
          } else {
            setMaterialOption(MaterialType.Black);
          }

          if (quotation.selectedLength) {
            setSelectedLength(quotation.selectedLength);
          }

          if (quotation.scale) {
            setScale(quotation.scale);
          }

          if (quotation.includeAttachement) {
            setIncludeAttachement(quotation.includeAttachement);
          }

          if (quotation.province) {
            if ((quotation.province as Province) === Province.Ontario) {
              i18n.changeLanguage("en");
            } else i18n.changeLanguage("fr");

            setProvince(quotation.province as Province);
          }
        }
      }
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    let maxX = Number.MIN_SAFE_INTEGER;
    let maxY = Number.MIN_SAFE_INTEGER;

    lines.forEach((line) => {
      maxX = Math.max(maxX, line.start.x, line.end.x);
      maxY = Math.max(maxY, line.start.y, line.end.y);
    });

    const canvas = canvasRef.current;
    if (canvas === null) return;
    const context = canvas.getContext("2d");

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const desiredWidth = Math.min(screenWidth * 0.9, 1000);
    const desiredHeight = Math.min(screenHeight * 0.7, 1000);
    canvas.width = desiredWidth;
    canvas.height = desiredHeight;

    if (!context) return;
    const { pannels, material } = renderChange(
      context,
      canvas,
      lines,
      parseInt(selectedLength),
      scale,
      clientDetails?.chalkLine || false,
      clientDetails?.gapSize || 1
    );

    const { finalCost, attachment, totalCost } = getFinalCost(
      pannels,
      materialOption,
      material,
      includeAttachement,
      lines.filter((line) => line.numberOfDoors > 1).length > 0,
      province // hard coded for now
    );

    setFinalCost(finalCost);
    setMaterial(attachment);
    setTotalCost(totalCost);
  }, [lines]);
  const handlePrint = () => {
    window.print();
  };
  return (
    <div className="page">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <HomeLink province={province} />
        <button className="edit-button" onClick={handlePrint}>
          {province === Province.Ontario ? "print" : "imprimer"}
        </button>
      </div>
      <div style={{ width: "100%", textAlign: "center" }} className="title">
        <div
          style={{
            display: "flex",
            alignContent: "space-between",
            justifyContent: "space-between",
          }}
        >
          <div>
            <p>
              {" "}
              {province === Province.Ontario
                ? "Quotation Number"
                : "Soumission"}{" "}
              #{slug}
            </p>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignContent: "space-between",
            justifyContent: "space-between",
          }}
        >
          <div style={{ textAlign: "start" }}>
            <img
              src={province === Province.Ontario ? LogoEnglish : Logo}
              alt="logo"
              height={100}
              style={{
                mixBlendMode: "multiply",
                objectFit: "cover",
                backgroundColor: "transparent",
                backgroundClip: "content-box",
              }}
            />
            <>
              <p>{t("companyAddress")}</p>
              <p>{t("companyAddress2")}</p>
              <p>{t("companyPhoneNumber")}</p>
              <p>{t("companyEmail")}</p>
            </>
          </div>
          <div style={{ textAlign: "start" }}>
            <p>Date: {date} </p>
            <p>
              {t("name")}: {clientDetails?.name}
            </p>
            <p>email: {clientDetails?.email?.toLowerCase()}</p>
            <p>
              {t("phone")}: {clientDetails?.phone}
            </p>
            <p>
              {t("address")}: {clientDetails?.address}
            </p>
            {clientDetails?.note && <p>Note: {clientDetails.note}</p>}
            {clientDetails?.chalkLine && <p>chalk line</p>}
            {clientDetails?.gapSize !== 1 && (
              <p>Gap Size: {clientDetails?.gapSize}"</p>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          alignContent: "space-around",
          justifyContent: "space-around",
          width: "100%",
        }}
      >
        <canvas
          className="canvas-container"
          ref={canvasRef}
          id="canvas"
          height="600px"
          width="600px"
          style={{
            backgroundColor: "antiquewhite",
            margin: "20px auto",
            justifySelf: "center",
          }}
        ></canvas>

        <div>
          <div>
            <div
              style={{ display: "flex", justifyContent: "space-between" }}
            ></div>
            <QuotationTable
              province={province}
              selectedLength={parseInt(selectedLength)}
              finalCost={finalCost}
              materialOption={materialOption}
              material={material}
              totalCost={totalCost}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationDetails;
